import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Menu,
  MenuItem,
  Snackbar,
  Typography,
} from '@mui/material'
import { AxiosError } from 'axios'

import { apiZodios } from '~api/apiZodios'
import { InputLabelStyled, PhoneEntryBox, TextInput } from '~components/common'
import { RootState } from '~redux/rootReducer'
import { HttpStatus } from '~types'

import { useAISettings } from '../AISettingsContext'

export type AIPhoneLineType = 'STABLE' | 'BETA' | 'ELEVEN_LABS'
export type AIPhoneLineOwnerType = 'CUSTOMER' | 'DEMO'

export const getPhoneLineTypeDisplay: (type: AIPhoneLineType) => string = (
  type: AIPhoneLineType,
) => {
  switch (type) {
    case 'STABLE':
      return 'Standard'
    case 'BETA':
      return 'Preview'
    case 'ELEVEN_LABS':
      return 'Premium'
  }
}

export const getPhoneLineOwnerTypeDisplay: (type: AIPhoneLineOwnerType) => string = (
  type: AIPhoneLineOwnerType,
) => {
  switch (type) {
    case 'CUSTOMER':
      return 'Customer'
    case 'DEMO':
      return 'Demo'
  }
}

interface IPhoneNumber {
  phoneNumber: string
}

interface AIPhoneLineDialogProps {
  open: boolean
  onClose: () => void
  onSuccess?: (successMessage: string) => void
  isUpdate: boolean
  aiPhoneLineId?: string
}

interface DropdownProps<T> {
  label: string
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
  id: string
}

function Dropdown<T extends string>({
  label,
  value,
  options,
  onChange,
  id,
}: DropdownProps<T>): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  return (
    <Box sx={{ mb: 1 }}>
      <InputLabelStyled htmlFor={id} shrink sx={{ fontSize: 20 }}>
        {label}
      </InputLabelStyled>
      <Box
        sx={{
          display: 'flex',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px',
          cursor: 'pointer',
        }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <Typography>{options.find((opt) => opt.value === value)?.label}</Typography>
      </Box>
      <Menu
        id={`${id}-menu`}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          'aria-labelledby': `${id}-menu-button`,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            width: 180,
            borderRadius: '8px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              onChange(option.value)
              setAnchorEl(null)
            }}
            selected={value === option.value}
            sx={{ py: 1 }}
          >
            <Typography>{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

const AIPhoneLineDialog: React.FC<AIPhoneLineDialogProps> = ({
  open,
  onClose,
  onSuccess,
  isUpdate,
  aiPhoneLineId,
}) => {
  const { selectedOrganization } = useAISettings()
  const user = useSelector((state: RootState) => state?.auth.user)
  const isSuper = useSelector((state: RootState) => state?.auth.user?.super)

  const [number, setNumber] = useState<string>('')
  const [name, setName] = useState<string>('')
  const [type, setType] = useState<AIPhoneLineType>('STABLE')
  const [ownerType, setOwnerType] = useState<AIPhoneLineOwnerType>('CUSTOMER')
  const [isValidPhone, setIsValidPhone] = useState<boolean>(false)
  const [isValidForm, setIsValidForm] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [snackbarMessage, setSnackbarMessage] = useState<string>('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'warning'>('warning')
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const typeOptions = [
    { value: 'STABLE' as AIPhoneLineType, label: getPhoneLineTypeDisplay('STABLE') },
    { value: 'BETA' as AIPhoneLineType, label: getPhoneLineTypeDisplay('BETA') },
    { value: 'ELEVEN_LABS' as AIPhoneLineType, label: getPhoneLineTypeDisplay('ELEVEN_LABS') },
  ]

  const ownerTypeOptions = [
    { value: 'CUSTOMER' as AIPhoneLineOwnerType, label: getPhoneLineOwnerTypeDisplay('CUSTOMER') },
    { value: 'DEMO' as AIPhoneLineOwnerType, label: getPhoneLineOwnerTypeDisplay('DEMO') },
  ]

  useEffect(() => {
    if (aiPhoneLineId) {
      fetchAIPhoneLine(aiPhoneLineId)
    } else {
      resetForm()
    }
  }, [aiPhoneLineId, open])

  const resetForm = (): void => {
    setNumber('')
    setType('STABLE')
    setOwnerType('CUSTOMER')
    setIsValidPhone(false)
    setError(null)
  }

  const fetchAIPhoneLine = async (aiPhoneLineId: string): Promise<void> => {
    if (!selectedOrganization?.id || !user) return
    try {
      const response = await apiZodios.ai_listAIPhoneLines({
        user_id: user.id,
        organization_identity_id: selectedOrganization.id,
      })

      const aiPhoneLine = response.ai_phone_lines.find((line) => line.id === aiPhoneLineId)
      if (!aiPhoneLine) return

      let formattedNumber = aiPhoneLine.number.startsWith('+1')
        ? aiPhoneLine.number.substring(2)
        : aiPhoneLine.number

      setNumber(formattedNumber)
      setType(aiPhoneLine.type)
      setOwnerType(aiPhoneLine.owner_type || 'CUSTOMER')
      setName(aiPhoneLine.name || '')
      setIsValidPhone(!!formattedNumber)
    } catch (error) {
      console.error('Error fetching AI phone line:', error)
    }
  }

  const handlePhoneNumberChange = (newPhoneString: string, newPhoneIsValid: boolean): void => {
    setNumber(newPhoneString)
    setIsValidPhone(newPhoneIsValid)
  }

  function getPhoneNumber(phoneString: string): IPhoneNumber | undefined {
    const cleanedNumber = phoneString.replace(/\D/g, '')
    if (cleanedNumber && cleanedNumber.length >= 10) {
      return {
        phoneNumber: cleanedNumber,
      }
    }
  }

  const validateAIPhoneLine = (): boolean => {
    if (!number) {
      setError('Phone number is required')
      return false
    }

    if (!isValidPhone) {
      setError('Invalid phone number format')
      return false
    }

    return true
  }

  const handleSave = async (): Promise<void> => {
    if (!validateAIPhoneLine()) return
    if (!selectedOrganization?.id || !user) return

    const numberComponents = getPhoneNumber(number)
    if (!numberComponents) {
      setError('Invalid phone number format')
      return
    }

    try {
      let successMessage = ''
      if (isUpdate) {
        if (!aiPhoneLineId) {
          return
        }
        setIsSaving(true)
        await apiZodios.ai_editAIPhoneLine({
          user_id: user.id,
          organization_identity_id: selectedOrganization.id,
          ai_phone_line_id: aiPhoneLineId,
          number: '+1' + numberComponents.phoneNumber,
          type: type,
          owner_type: ownerType,
          name: name || '',
        })
        successMessage = 'Successfully updated AI phone line'
      } else {
        setIsSaving(true)
        await apiZodios.ai_addAIPhoneLine({
          user_id: user.id,
          organization_identity_id: selectedOrganization.id,
          number: '+1' + numberComponents.phoneNumber,
          type: type,
          owner_type: ownerType,
          name: name || '',
        })
        successMessage = 'Successfully added AI phone line'
      }
      setIsSaving(false)
      onSuccess?.(successMessage)
      onClose()
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        if (error.response?.status === HttpStatus.CONFLICT) {
          setSnackbarMessage('Phone number already exists')
          setSnackbarSeverity('warning')
        } else {
          setSnackbarMessage('Failed to save AI phone line')
          setSnackbarSeverity('error')
        }
        setIsSnackbarOpen(true)
        setIsSaving(false)
      }
    }
  }

  useEffect(() => {
    const isValid = !!number && isValidPhone
    setIsValidForm(isValid)
  }, [number, isValidPhone])

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md'>
      <Box className='flex-v-align' sx={{ ml: 2, mr: 2, mt: 2, borderBottom: '1px solid #ccc' }}>
        <Typography variant='h1' sx={{ p: 1, mb: 2 }}>
          {isUpdate ? 'Edit AI Phone Line' : 'Add AI Phone Line'}
        </Typography>
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            ml: 'auto',
          }}
        >
          <Button color='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='secondary'
            onClick={handleSave}
            disabled={!isValidForm}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {isUpdate ? 'Update Phone Line' : 'Save Phone Line'}
          </Button>
        </Box>
      </Box>
      <DialogContent>
        <Box
          noValidate
          component='form'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            m: 'auto',
            width: '100%',
          }}
        >
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 1 }}>
            <InputLabelStyled htmlFor='text-input' shrink sx={{ fontSize: 20 }}>
              Phone Number
            </InputLabelStyled>
            <PhoneEntryBox inputPhoneState={number} onChange={handlePhoneNumberChange} />
          </Box>

          <Box sx={{ mb: 1 }}>
            <InputLabelStyled htmlFor='text-input' shrink sx={{ fontSize: 20 }}>
              Name
            </InputLabelStyled>
            <TextInput
              placeholder='Enter a name for the phone line'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>

          <Dropdown
            label='Type'
            value={type}
            options={typeOptions}
            onChange={setType}
            id='type-select'
          />

          {isSuper && (
            <Dropdown
              label='Owner Type'
              value={ownerType}
              options={ownerTypeOptions}
              onChange={setOwnerType}
              id='owner-type-select'
            />
          )}
        </Box>

        <Snackbar
          open={isSnackbarOpen}
          autoHideDuration={3000}
          onClose={() => setIsSnackbarOpen(false)}
        >
          <Alert onClose={() => setIsSnackbarOpen(false)} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {isSaving && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
            }}
          >
            <CircularProgress size={48} />
            <Typography sx={{ mt: 2 }}>
              {isUpdate ? 'Updating AI Phone Line...' : 'Adding AI Phone Line...'}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AIPhoneLineDialog
